/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import Raphael from 'raphael';
import TarjetaEstado from './TarjetaEstado';
import TarjetaAliado from './TarjetaAliado';
import ModalDistribuidoras from './ModalDistribuidoras';
import {
  getMapPaths,
  datosEstados,
  datosAliados,
  estados,
  estadoAlias,
} from '../../../../utils';
import '../../styles/coberturaNacional/mapa.css';

const Cobertura = () => {
  const paperRef = useRef(null);
  const [estadoActivo, setEstadoActivo] = useState(null);
  const [estadoHover, setEstadoHover] = useState(null);
  const raphaelRef = useRef(null);
  const mapElementsRef = useRef({});
  const [mostrarPopup, setMostrarPopup] = useState(false);

  // Saber si un estado tiene teléfonos o asesores
  const tieneContacto = stateName => {
    const tieneTelefonos =
      datosEstados[stateName] &&
      Array.isArray(datosEstados[stateName].telefonos) &&
      datosEstados[stateName].telefonos.length > 0;

    const tieneAsesores =
      datosAliados[stateName] &&
      Array.isArray(datosAliados[stateName].asesores) &&
      datosAliados[stateName].asesores.length > 0;

    return tieneTelefonos || tieneAsesores;
  };

  const highlightState = useCallback(stateName => {
    if (mapElementsRef.current[stateName]) {
      // Resetear todos los estados a su color base
      Object.keys(mapElementsRef.current).forEach(key => {
        mapElementsRef.current[key].animate(
          { fill: '#ffffff', stroke: '#DB197F' },
          500
        );
      });
      // Destacar el estado seleccionado
      mapElementsRef.current[stateName].animate(
        { fill: '#EE5BA0', stroke: '#ccc' },
        500
      );
      mapElementsRef.current[stateName].toFront();
    }
  }, []);

  const resetAllStates = useCallback(() => {
    Object.keys(mapElementsRef.current).forEach(key => {
      mapElementsRef.current[key].animate(
        { fill: '#ffffff', stroke: '#DB197F' },
        500
      );
    });
  }, []);

  // Función para manejar el hover en el mapa
  const handleMapHover = useCallback(stateName => {
    if (!tieneContacto(stateName)) return;
    setEstadoHover(stateName);
  }, []);

  // Función para limpiar el hover en el mapa
  const handleMapHoverOut = useCallback(() => {
    setEstadoHover(null);
  }, []);

  useEffect(() => {
    if (raphaelRef.current) {
      raphaelRef.current.remove();
    }

    const R = Raphael(paperRef.current, 900, 700);
    raphaelRef.current = R;

    const attr = {
      fill: '#ffffff',
      stroke: '#DB197F',
      'stroke-width': 1,
      'stroke-linejoin': 'round',
    };

    const ven = getMapPaths(R, attr);
    mapElementsRef.current = ven;

    for (const state in ven) {
      ven[state].color = '#EE5BA0';

      (function (st, stateName) {
        const tiene = tieneContacto(stateName);

        st[0].style.cursor = tiene ? 'pointer' : 'default';

        st[0].onmouseover = function () {
          if (!tiene) return;

          // Usar la función de callback para manejar hover
          handleMapHover(stateName);

          // Solo cambiar color si no es el estado activo
          if (stateName !== estadoActivo) {
            st.animate({ fill: st.color, stroke: '#ccc' }, 500);
            st.toFront();
          }
        };

        st[0].onmouseout = function () {
          if (!tiene) return;

          // Usar la función de callback para limpiar hover
          handleMapHoverOut();

          // Solo resetear si no es el estado activo
          if (stateName !== estadoActivo) {
            st.animate({ fill: '#ffffff', stroke: '#DB197F' }, 500);
          }
        };

        st[0].onclick = function () {
          if (!tiene) return;
          // Si es el mismo estado, lo deselecciona, si no, lo selecciona
          const nuevoEstado = stateName === estadoActivo ? null : stateName;
          setEstadoActivo(nuevoEstado);
        };
      })(ven[state], state);
    }

    // Aplicar el highlight al estado activo si existe
    if (estadoActivo) {
      highlightState(estadoActivo);
    }

    return () => {
      if (raphaelRef.current) {
        raphaelRef.current.remove();
      }
    };
  }, [
    estadoActivo,
    highlightState,
    tieneContacto,
    handleMapHover,
    handleMapHoverOut,
  ]);

  // Effect separado para manejar cambios en el estado activo
  useEffect(() => {
    setMostrarPopup(false);
    if (estadoActivo) {
      highlightState(estadoActivo);
    } else {
      resetAllStates();
    }
  }, [estadoActivo, highlightState, resetAllStates]);

  // Función mejorada para manejar hover en botones
  const handleButtonHover = useCallback(estado => {
    if (!tieneContacto(estado)) return;
    setEstadoHover(estado);
  }, []);

  // Función para limpiar hover en botones
  const handleButtonHoverOut = useCallback(() => {
    setEstadoHover(null);
  }, []);

  const handleFlechaClick = useCallback(() => {
    // Usar el estado activo o el estado hover para abrir modal
    const estadoParaModal = estadoActivo || estadoHover;

    // Abrir modal solo si hay distribuidoras o asesores
    if (
      !estadoParaModal ||
      (!datosAliados[estadoParaModal]?.distribuidoras?.length &&
        !datosAliados[estadoParaModal]?.asesores?.length)
    ) {
      setMostrarPopup(false);
      return;
    }
    setMostrarPopup(prev => !prev);
  }, [estadoActivo, estadoHover]);

  const tieneDatosAliadosValidos = estado => {
    if (!datosAliados[estado]) return false;

    const distribuidoras = datosAliados[estado].distribuidoras || [];
    const asesores = datosAliados[estado].asesores || [];

    const tieneDatosValidos = arr =>
      arr.some(
        item =>
          item.nombre?.trim() !== '' ||
          item.email?.trim() !== '' ||
          item.direccion?.trim() !== '' ||
          item.telefono?.trim() !== ''
      );

    return tieneDatosValidos(distribuidoras) || tieneDatosValidos(asesores);
  };

  // Determinar qué estado mostrar: activo tiene prioridad sobre hover
  const estadoAMostrar = estadoActivo || estadoHover;

  return (
    <section className="coverage-section">
      <div className="main-layout">
        <div className="left-panel">
          <div className="boxes-wrapper">
            <div className="header">
              <h1>
                <span className="bold">
                  Cobertura <br /> en más{' '}
                </span>
                de <br />
                <span className="pink bold">16 estados</span>
                <br />
              </h1>
            </div>
            <h2 className="estado-nombre">
              {estadoAMostrar
                ? estadoAlias[estadoAMostrar] || estadoAMostrar
                : ''}
            </h2>
            <div className="pink-box">
              {estadoAMostrar && datosEstados[estadoAMostrar] && (
                <TarjetaEstado
                  estado={estadoAlias[estadoAMostrar] || estadoAMostrar}
                  tipo={datosEstados[estadoAMostrar].tipo}
                  name={datosEstados[estadoAMostrar].name}
                  telefonos={datosEstados[estadoAMostrar].telefonos}
                />
              )}
            </div>
            <div className="gray-box" style={{ position: 'relative' }}>
              {estadoAMostrar && tieneDatosAliadosValidos(estadoAMostrar) && (
                <>
                  <TarjetaAliado onFlechaClick={handleFlechaClick} />
                  {mostrarPopup && <ModalDistribuidoras visible={true} />}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="map-wrapper">
          <div className="map-container" ref={paperRef}></div>
        </div>
      </div>
      <div className="estado-buttons">
        <div className="estado-row">
          {estados.slice(0, 11).map(estado => (
            <button
              key={estado}
              className={`estado-btn estado-btn-small ${estado === estadoActivo ? 'activo' : ''} ${estado === estadoHover && estado !== estadoActivo ? 'hover' : ''}`}
              onClick={() =>
                setEstadoActivo(estado === estadoActivo ? null : estado)
              }
              onMouseEnter={() => handleButtonHover(estado)}
              onMouseLeave={handleButtonHoverOut}
              disabled={!tieneContacto(estado)}
            >
              {estadoAlias[estado] || estado}
            </button>
          ))}
        </div>
        <div className="estado-row">
          {estados.slice(11, 24).map(estado => (
            <button
              key={estado}
              className={`estado-btn ${estado === estadoActivo ? 'activo' : ''} ${estado === estadoHover && estado !== estadoActivo ? 'hover' : ''}`}
              onClick={() =>
                setEstadoActivo(estado === estadoActivo ? null : estado)
              }
              onMouseEnter={() => handleButtonHover(estado)}
              onMouseLeave={handleButtonHoverOut}
              disabled={!tieneContacto(estado)}
            >
              {estadoAlias[estado] || estado}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Cobertura;
